<template>
  <page-wrapper :title="title">
    <appointment-filter-bar
      v-model="filterForm"
      :is-downloading="isDownloading"
      @search="handleSearch"
      @download="downloadReport"
    ></appointment-filter-bar>
    <p v-if="total" class="ps-5 pe-5 mb-2 mt-2">{{ totalCount }}</p>
    <appointment-table :data="list"></appointment-table>
    <div v-if="total" class="text-end mt-4">
      <el-pagination
        :total="total"
        :size="10"
        :current-page="queryParams.page"
        background
        layout="prev, pager, next"
        @current-change="handleCurrentChange"
      ></el-pagination>
    </div>
  </page-wrapper>
</template>

<script>
import { Pagination } from "element-ui";
import VueRouter from "vue-router";
import PageWrapper from "@/lib/components/PageWrapper";
import { DateTime } from "luxon";
import { baanRueJaiClient } from "@/dependencies";
import { getDurationAppointmentTimeText } from "@/lib/dateTime";
import { serviceType } from "@/lib/mapper";
import AppointmentFilterBar from "../components/AppointmentFilterBar";
import AppointmentTable from "../components/AppointmentTable";

export default {
  components: {
    PageWrapper,
    AppointmentTable,
    AppointmentFilterBar,
    "el-pagination": Pagination
  },
  data() {
    const isProductPage = this.$route.query
      && this.$route.query.pageType === "PRODUCT"

    const title = isProductPage
      ? "รายการสินค้า"
      : "รายการนัดหมาย";
    const appointments = [];
    const filterForm = {
      dateTimeRange: null,
      createdAtRange: null,
      provider: null,
      project: null,
      service: null,
      home: null,
      status: this.$route.query.status || null,
      user: null,
      group: []
    };
    const total = 0;

    return {
      title,
      appointments,
      filterForm,
      total,
      isProductPage,
      isDownloading: false
    };
  },
  computed: {
    totalCount() {
      const limit = 10;
      const total = this.total.toLocaleString();
      const currentTotalItem = this.list.length;
      const isLastPage = limit > currentTotalItem;
      const currentPage = this.queryParams.page;
      const end = (isLastPage ? total : currentPage * limit).toLocaleString();
      const start = (
        (isLastPage ? limit * currentPage - limit : end - limit) + 1
      ).toLocaleString();

      return `แสดงผล ${start} - ${end} จากทั้งหมด ${total}`;
    },
    queryParams() {
      return {
        page: +this.$route.query.page || 1,
        createdAtFrom: this.$route.query.createdAtFrom,
        createdAtTo: this.$route.query.createdAtTo,
        dateTimeFrom: this.$route.query.dateTimeFrom,
        dateTimeTo: this.$route.query.dateTimeTo,
        homeId: this.$route.query.homeId,
        serviceId: this.$route.query.serviceId,
        providerId: this.$route.query.providerId,
        projectId: this.$route.query.projectId,
        status: this.$route.query.status,
        userId: this.$route.query.userId
      };
    },
    list() {
      return this.appointments.map(appointment => {
        const {
          id,
          date_time: dateTime,
          status,
          service,
          provider,
          project,
          created_at: createdAt,
          user,
          home,
          package_name: packageName,
          main_option: mainOption,
          extra_option: extraOption,
          date_time_end: dateTimeEnd,
          member_subscription_id: memberSubscriptionId
        } = appointment;

        const appointmentDurationTime = getDurationAppointmentTimeText(
          dateTime || null,
          dateTimeEnd || null
        )

        return {
          id: id.toString(),
          dateTime: dateTime ? DateTime.fromISO(dateTime).toUTC() : null,
          status,
          service: {
            id: service.id.toString(),
            name: service.name,
            shortDescription: service.short_description
          },
          provider: {
            name: provider.name
          },
          home: {
            address: `บ้านเลขที่ ${home.address_no}`,
            project: project.name,
            contact: {
              name: `${user.first_name} ${user.last_name}`,
              phoneNumber: user.mobile_number || "-"
            }
          },
          route: status === "EMPTY"
            ? { name: "SubscriberDetailPage", params: { id: memberSubscriptionId } }
            : { name: "AppointmentDetailPage", params: { id } },
          createdAt: DateTime.fromISO(createdAt).toUTC(),
          packageName,
          mainOption: mainOption || "",
          extraOption: extraOption || "",
          appointmentDurationTime
        };
      });
    },
    queryBody() {
      const params = {
        page: this.queryParams.page
      };

      if (this.queryParams.dateTimeFrom && this.queryParams.dateTimeTo) {
        params.dateTime = {
          from: this.queryParams.dateTimeFrom,
          to: this.queryParams.dateTimeTo
        };
      }

      if (this.queryParams.homeId) {
        params.homeId = this.queryParams.homeId;
      }

      if (this.queryParams.createdAtFrom && this.queryParams.createdAtTo) {
        params.createdAt = {
          from: this.queryParams.createdAtFrom,
          to: this.queryParams.createdAtTo
        };
      }

      if (this.queryParams.projectId) {
        params.projectId = this.queryParams.projectId;
      }

      if (this.queryParams.providerId) {
        params.providerId = this.queryParams.providerId;
      }

      if (this.queryParams.serviceId) {
        params.serviceId = this.queryParams.serviceId;
      }

      if (this.queryParams.status) {
        params.status = this.queryParams.status;
      }

      if (this.queryParams.userId) {
        params.userId = +this.queryParams.userId;
      }

      if (this.filterForm.group && this.filterForm.group.length > 0) {
        params.groupPackageIds = this.filterForm.group.map(g => g.id);
      }

      params.type = this.$route.query && this.$route.query.pageType === serviceType.PRODUCT
        ? serviceType.PRODUCT
        : serviceType.SERVICE

      return params;
    }
  },
  watch: {
    "$route.query": function watchRouteQuery() {
      this.setupAppointments();
    }
  },
  created() {
    this.setup();
  },
  methods: {
    setup() {
      this.setupData();
    },
    setupData() {
      this.setupAppointments();
    },
    async setupAppointments() {
      const { count, appointments } = await this.fetchAppointments();

      this.total = count;
      this.appointments = appointments;
    },
    async handleSearch() {
      const query = {};

      const {
        dateTimeRange,
        createdAtRange,
        provider,
        project,
        service,
        home,
        status,
        user
      } = this.filterForm;

      if (dateTimeRange) {
        query.dateTimeFrom = dateTimeRange.from.toISO();
        query.dateTimeTo = dateTimeRange.to.toISO();
      }

      if (home) {
        query.homeId = home.homeId;
      }

      if (createdAtRange) {
        query.createdAtFrom = createdAtRange.from.toISO();
        query.createdAtTo = createdAtRange.to.toISO();
      }

      if (project) {
        query.projectId = project.projectId;
      }

      if (provider) {
        query.providerId = provider.id;
      }

      if (service) {
        query.serviceId = service.id;
      }

      if (status) {
        query.status = status;
      }

      if (user) {
        query.userId = user.user_id;
      }

      const route = {
        name: this.$route.name,
        query: { pageType: this.$route.query.pageType, ...query, page: 1 }
      };

      const result = await this.$router.replace(route);

      // The global router patch (see src/router/index.js) resolves rather
      // than rejects for a redundant navigation, so the $route.query watcher
      // won't fire in that case. Refetch manually so re-running the same
      // search still refreshes results.
      if (VueRouter.isNavigationFailure(result)) {
        this.setupAppointments();
      }
    },
    handleCurrentChange(page) {
      const route = {
        name: this.$route.name,
        query: { ...this.$route.query, page }
      };

      this.$router.replace(route);
    },
    async fetchAppointments() {
      const params = this.queryBody;

      const response = await baanRueJaiClient.admin.subscriptions.appointments.list(
        params
      );

      return response.data.result;
    },
    async downloadReport() {
      this.isDownloading = true;
      try {
        const params = this.queryBody;

        const response = await baanRueJaiClient.admin.subscriptions.appointments.downloadReport(
          params
        );

        const timestamp = DateTime.local().toFormat("yyyyMMdd-HHmmss");
        const blob = new Blob([response.data]);
        const link = document.createElement("a");
        link.href = window.URL.createObjectURL(blob);
        link.download = `appointments-report-${timestamp}.xlsx`;
        link.click();
        link.remove();
      } catch {
        alert("ไม่สามารถ Download Report ได้ เนื่องจากไม่มีข้อมูลหรือเกิดข้อผิดพลาด");
      } finally {
        this.isDownloading = false;
      }
    }
  }
};
</script>

<style lang="scss" scoped>
@import "~@/lib/scss/variables";

.filter-bar {
  padding: $space;
}
</style>
